import { Anchor, Button, Group, Select, Text, Textarea, TextInput } from '@mantine/core';
import { isCancelled, minutesToHm } from '@reditelji/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useChangelog, useShiftMutations } from '../api/hooks';
import type { Shift, TimelinePayload, TimelinePost } from '../api/types';
import { latestFreed, recordedAt } from '../volunteers/cancelCopy';
import { useAxis } from './axisContext';
import type { Axis } from './geometry';

/**
 * Quarter-hour options for an izmena's Od/Do.
 *
 * Bounded by the post's opening window, so the form offers exactly what
 * dragging the block's edge offers — otherwise one affordance refuses what the
 * other allows on the same shift. Whatever the shift already holds is always
 * included: a time outside the range would render as a blank Select rather
 * than as itself, and Mesta can move a window out from under a shift.
 */
export function timeOptions(
  bounds: { fromMin: number; toMin: number },
  ...include: (number | null)[]
): { value: string; label: string }[] {
  const mins = new Set<number>();
  const first = Math.ceil(bounds.fromMin / 15) * 15;
  for (let m = first; m <= bounds.toMin; m += 15) mins.add(m);
  for (const m of include) if (m !== null) mins.add(m);
  return [...mins]
    .sort((a, b) => a - b)
    .map((m) => ({ value: String(m), label: minutesToHm(m) }));
}

/** The window an izmena belongs to, falling back to the whole board. */
export function shiftWindow(post: TimelinePost, shift: Shift, axis: Axis): { fromMin: number; toMin: number } {
  const overlaps = (w: { fromMin: number; toMin: number }) =>
    w.fromMin < shift.toMin && shift.fromMin < w.toMin;
  return (
    post.windows.find((w) => w.fromMin <= shift.fromMin && shift.toMin <= w.toMin) ??
    post.windows.find(overlaps) ??
    axis
  );
}

/**
 * A post's code and name in a side panel heading, as the way to the post
 * itself on Mesta.
 */
export function PostLink({ postId, children }: { postId: string; children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="tl-postlink"
      title="Odpri mesto"
      onClick={() => void navigate(`/mesta?post=${postId}`)}
    >
      {children}
    </button>
  );
}

/**
 * "↩ Sproščeno …" on an open slot whose holder is not coming (SPEC §1.1K):
 * why it is open, and the way to their card. Said only while it is still true —
 * a later assignment, a hand-made Sprosti or their return all silence it.
 */
export function FreedNote({ shiftId, data }: { shiftId: string; data: TimelinePayload }) {
  const navigate = useNavigate();
  const log = useChangelog('shift', shiftId);
  const freed = latestFreed(log.data?.entries ?? []);
  const holder = freed ? data.volunteers.find((v) => v.id === freed.volunteerId) : undefined;
  if (!freed || !holder || !isCancelled(holder)) return null;
  return (
    <Group
      gap={7}
      wrap="nowrap"
      align="flex-start"
      style={{ background: 'oklch(0.965 0.005 95)', borderRadius: 7, padding: '7px 9px' }}
    >
      <Text size="xs" fw={800} c="dimmed">
        ↩
      </Text>
      <Text size="xs" style={{ minWidth: 0, lineHeight: 1.45 }}>
        Sproščeno {recordedAt(freed.at)} — <b>{freed.name}</b> ne pride.{' '}
        <Anchor
          component="button"
          size="xs"
          onClick={() => void navigate(`/prostovoljci?volunteer=${freed.volunteerId}`)}
        >
          Odpri profil
        </Anchor>
      </Text>
    </Group>
  );
}

/**
 * An open row's own details — its times, meeting point and note — behind the
 * candidates that are the reason to open it. Nobody stands there, so a save
 * here asks nothing of anyone.
 */
export function OpenShiftEditor({
  post,
  shift,
  silent,
}: {
  post: TimelinePost;
  shift: Shift;
  silent: boolean;
}) {
  const axis = useAxis();
  const { update } = useShiftMutations();
  const [fromMin, setFromMin] = useState(String(shift.fromMin));
  const [toMin, setToMin] = useState(String(shift.toMin));
  const [meetMin, setMeetMin] = useState(shift.meetMin !== null ? String(shift.meetMin) : null);
  const [meetPlace, setMeetPlace] = useState(shift.meetPlace);
  const [note, setNote] = useState(shift.note);
  const window = shiftWindow(post, shift, axis);
  const dirty =
    Number(fromMin) !== shift.fromMin ||
    Number(toMin) !== shift.toMin ||
    (meetMin !== null ? Number(meetMin) : null) !== shift.meetMin ||
    meetPlace !== shift.meetPlace ||
    note !== shift.note;
  const reset = () => {
    setFromMin(String(shift.fromMin));
    setToMin(String(shift.toMin));
    setMeetMin(shift.meetMin !== null ? String(shift.meetMin) : null);
    setMeetPlace(shift.meetPlace);
    setNote(shift.note);
  };

  return (
    <>
      <Group gap={6} grow>
        <Select size="xs" label="Od" data={timeOptions(window, shift.fromMin)} value={fromMin} onChange={(v) => v && setFromMin(v)} searchable />
        <Select size="xs" label="Do" data={timeOptions(window, shift.toMin)} value={toMin} onChange={(v) => v && setToMin(v)} searchable />
      </Group>
      <Group gap={6} grow>
        <Select
          size="xs"
          label="Zbor ob"
          placeholder="—"
          data={timeOptions(axis, shift.meetMin)}
          value={meetMin}
          onChange={setMeetMin}
          searchable
          clearable
        />
        <TextInput size="xs" label="Zborno mesto" value={meetPlace} onChange={(e) => setMeetPlace(e.currentTarget.value)} />
      </Group>
      <Textarea size="xs" label="Zabeležka" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={1} />
      {dirty ? (
        <div className="tl-detail-savebar">
          <Text size="xs" c="dimmed" style={{ flex: 1 }}>
            Neshranjeno
          </Text>
          <Button size="compact-sm" variant="subtle" color="gray" onClick={reset}>
            Prekliči
          </Button>
          <Button
            size="compact-sm"
            loading={update.isPending}
            onClick={() =>
              update.mutate({
                id: shift.id,
                updatedAt: shift.updatedAt,
                fromMin: Number(fromMin),
                toMin: Number(toMin),
                meetMin: meetMin !== null ? Number(meetMin) : null,
                meetPlace,
                note,
                silent,
              })
            }
          >
            Shrani
          </Button>
        </div>
      ) : null}
    </>
  );
}
